// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Team management orchestration
// 2. Separation of Concerns - UI separated from business logic
// 3. Modular Architecture - Reusable team management components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import teamManagementService from '../../services/teamManagementService';
import TeamList from './TeamList';
import TeamForm from './TeamForm';
import TeamMemberManagement from './TeamMemberManagement';
import AlertDialog from '../common/AlertDialog';
import PageSectionHeader from '../common/PageSectionHeader';

const TeamManagement = () => {
  
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showMemberManagement, setShowMemberManagement] = useState(false);
  
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info'
  });

  // Load teams on mount and when showInactive changes
  useEffect(() => {
    loadTeams();
  }, [showInactive]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamManagementService.listTeams(showInactive);
      setTeams(data.teams || []); // Extract teams array from response
    } catch (err) {
      console.error('Failed to load teams:', err);
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = () => {
    setSelectedTeam(null);
    setShowTeamForm(true);
  };

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setShowTeamForm(true);
  };

  const handleManageMembers = (team) => {
    setSelectedTeam(team);
    setShowMemberManagement(true);
  };

  const handleCloseTeamForm = () => {
    setShowTeamForm(false);
    setSelectedTeam(null);
  };

  const handleCloseMemberManagement = () => {
    setShowMemberManagement(false);
    setSelectedTeam(null);
  };

  const handleTeamSaved = () => {
    loadTeams();
    handleCloseTeamForm();
    setAlertDialog({
      open: true,
      title: 'Success',
      message: selectedTeam ? 'Team updated successfully!' : 'Team created successfully!',
      severity: 'success'
    });
  };

  const handleTeamDeleted = () => {
    loadTeams();
    setAlertDialog({
      open: true,
      title: 'Success',
      message: 'Team deleted successfully!',
      severity: 'success'
    });
  };

  const handleReactivateTeam = async (team) => {
    try {
      await teamManagementService.reactivateTeam(team.id);
      loadTeams();
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Team "${team.name}" reactivated successfully!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to reactivate team:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to reactivate team',
        severity: 'error'
      });
    }
  };

  const handleMembersSaved = () => {
    loadTeams();
    setAlertDialog({
      open: true,
      title: 'Success',
      message: 'Team members updated successfully!',
      severity: 'success'
    });
  };

  return (
    <Box>
      <PageSectionHeader
        icon={<GroupsIcon />}
        title="Team Management"
        description="Create teams, assign members, and manage team visibility"
        actions={(
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTeams}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTeam}
              disabled={loading}
            >
              Create Team
            </Button>
          </>
        )}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Show Inactive Toggle */}
      {!loading && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                color="primary"
              />
            }
            label="Show inactive teams"
          />
        </Paper>
      )}

      {/* Team List */}
      {!loading && (
        <TeamList
          teams={teams}
          onEdit={handleEditTeam}
          onManageMembers={handleManageMembers}
          onDelete={handleTeamDeleted}
          onReactivate={handleReactivateTeam}
          onRefresh={loadTeams}
          showInactive={showInactive}
        />
      )}

      {/* Team Form Dialog */}
      {showTeamForm && (
        <TeamForm
          open={showTeamForm}
          team={selectedTeam}
          onClose={handleCloseTeamForm}
          onSave={handleTeamSaved}
        />
      )}

      {/* Member Management Dialog */}
      {showMemberManagement && selectedTeam && (
        <TeamMemberManagement
          open={showMemberManagement}
          team={selectedTeam}
          onClose={handleCloseMemberManagement}
          onSave={handleMembersSaved}
        />
      )}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Box>
  );
};

export default TeamManagement;
